import React, { useMemo } from "react";
import { toJS } from "mobx";
import { Box } from "@mui/material";
import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";
import { ECHARTS_THEME } from "./echartsTheme";
import { DailyStat } from "../../types/claims";

interface MiniLineChartProps {
  data: DailyStat[] | null;
  dataKey:
    | "total_files"
    | "total_claims_found"
    | "total_claims_processed"
    | "success_rate";
  color?: string;
  currentValue?: number;
}

type ChartDataPoint = {
  date: string;
  value: number;
};

const CHART_HEIGHT = 70;

const formatTooltipDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return `${date.getMonth() + 1}/${date.getDate()}`;
};

const formatTooltipValue = (
  value: number,
  dataKey: MiniLineChartProps["dataKey"],
): string =>
  dataKey === "success_rate" ? `${value.toFixed(1)}%` : value.toLocaleString();

const buildChartOption = (
  chartData: ChartDataPoint[],
  dataKey: MiniLineChartProps["dataKey"],
  color: string,
): EChartsOption => ({
  animation: false,
  grid: { top: 5, right: 8, left: 8, bottom: 5, containLabel: false },
  xAxis: {
    type: "category",
    data: chartData.map((point) => point.date),
    show: false,
    boundaryGap: false,
  },
  yAxis: {
    type: "value",
    show: false,
    scale: true,
  },
  tooltip: {
    trigger: "axis",
    confine: true,
    appendTo: "body",
    backgroundColor: "#1e1e1e",
    borderColor: "#333",
    borderWidth: 1,
    padding: [4, 8],
    textStyle: { color: "#fff", fontSize: 10 },
    formatter: (params: unknown) => {
      const items = (Array.isArray(params) ? params : [params]) as {
        axisValue?: string;
        value?: number | string;
      }[];
      const point = items[0];
      if (!point?.axisValue) return "";

      const num = Number(point.value ?? 0);
      return `${formatTooltipDate(String(point.axisValue))}<br/>${formatTooltipValue(num, dataKey)}`;
    },
  },
  series: [
    {
      type: "line",
      data: chartData.map((point) => point.value),
      smooth: true,
      showSymbol: false,
      symbol: "circle",
      symbolSize: 6,
      lineStyle: { color, width: 2 },
      itemStyle: { color },
      emphasis: {
        focus: "series",
        itemStyle: { color, borderColor: color },
      },
    },
  ],
});

const MiniLineChart: React.FC<MiniLineChartProps> = ({
  data,
  dataKey,
  color = "#82ca9d",
  currentValue,
}) => {
  const chartData = useMemo((): ChartDataPoint[] => {
    const createFlatLine = (value: number): ChartDataPoint[] => {
      const today = new Date();
      const flatData: ChartDataPoint[] = [];

      for (let i = 29; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        date.setHours(0, 0, 0, 0);

        flatData.push({
          date: date.toISOString().split("T")[0],
          value,
        });
      }

      return flatData;
    };

    if (!data || data.length === 0) {
      if (currentValue !== undefined && currentValue !== null) {
        return createFlatLine(currentValue);
      }
      return [];
    }

    const dailyStats = toJS(data);

    const sortedDays = dailyStats
      .slice()
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    if (sortedDays.length < 30) {
      const existingDates = new Set(sortedDays.map((s) => s.date));
      const allDays: ChartDataPoint[] = [];

      sortedDays.forEach((stat) => {
        let value: number;
        if (dataKey === "success_rate") {
          value =
            stat.total_claims_found > 0
              ? (stat.total_claims_processed / stat.total_claims_found) * 100
              : 0;
        } else if (dataKey === "total_files") {
          value = stat.total_files;
        } else if (dataKey === "total_claims_found") {
          value = stat.total_claims_found;
        } else {
          value = stat.total_claims_processed;
        }

        allDays.push({
          date: stat.date,
          value,
        });
      });

      allDays.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      );

      const lastValue =
        allDays.length > 0
          ? allDays[allDays.length - 1].value
          : currentValue !== undefined && currentValue !== null
            ? currentValue
            : 0;

      if (allDays.length > 0) {
        const firstDate = new Date(allDays[0].date);

        for (let i = 0; i < 30; i++) {
          const date = new Date(firstDate);
          date.setDate(date.getDate() + i);
          const dateStr = date.toISOString().split("T")[0];

          if (!existingDates.has(dateStr)) {
            allDays.push({
              date: dateStr,
              value: lastValue,
            });
          }
        }
      }

      return allDays.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      );
    }

    const first30Days = sortedDays.slice(0, 30);
    const mappedData = first30Days
      .map((stat): ChartDataPoint => {
        let value: number;
        if (dataKey === "success_rate") {
          value =
            stat.total_claims_found > 0
              ? (stat.total_claims_processed / stat.total_claims_found) * 100
              : 0;
        } else if (dataKey === "total_files") {
          value = stat.total_files;
        } else if (dataKey === "total_claims_found") {
          value = stat.total_claims_found;
        } else {
          value = stat.total_claims_processed;
        }

        return {
          date: stat.date,
          value,
        };
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    if (mappedData.length < 30) {
      const existingDates = new Set(mappedData.map((d) => d.date));
      const filledData = [...mappedData];

      const lastValue =
        mappedData.length > 0
          ? mappedData[mappedData.length - 1].value
          : currentValue !== undefined && currentValue !== null
            ? currentValue
            : 0;

      const firstDate =
        mappedData.length > 0 ? new Date(mappedData[0].date) : new Date();

      for (let i = 0; i < 30; i++) {
        const date = new Date(firstDate);
        date.setDate(date.getDate() + i);
        const dateStr = date.toISOString().split("T")[0];

        if (!existingDates.has(dateStr)) {
          filledData.push({
            date: dateStr,
            value: lastValue,
          });
        }
      }

      return filledData.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      );
    }

    return mappedData;
  }, [data, dataKey, currentValue]);

  const chartOption = useMemo(
    () =>
      chartData.length > 0
        ? buildChartOption(chartData, dataKey, color)
        : undefined,
    [chartData, dataKey, color],
  );

  if (!chartOption) {
    return null;
  }

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
      <ReactECharts
        option={chartOption}
        theme={ECHARTS_THEME}
        style={{ height: CHART_HEIGHT, width: "100%" }}
        notMerge
        lazyUpdate
      />
    </Box>
  );
};

export default MiniLineChart;
